import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { FacturaCompra } from '../types/facturaCompra.types';
import { FacturaCompraService } from '../services/facturaCompraService';

interface FacturaCompraState {
  items: FacturaCompra[];
  selectedItem: FacturaCompra | null;
  loading: boolean;
  error: string | null;

  fetchAll: () => Promise<void>;
  fetchById: (id: number) => Promise<void>;
  save: (data: FacturaCompra) => Promise<void>;
  remove: (id: number) => Promise<void>;
  setSelectedItem: (item: FacturaCompra | null) => void;
  clearError: () => void;
  reset: () => void;
}

const initialState = {
  items: [],
  selectedItem: null,
  loading: false,
  error: null,
};

export const useFacturaCompraStore = create<FacturaCompraState>()(
  devtools(
    (set) => ({
      ...initialState,

      fetchAll: async () => {
        set({ loading: true, error: null }, false, 'fc/fetchAll/start');
        try {
          const items = await FacturaCompraService.getAll();
          set({ items, loading: false }, false, 'fc/fetchAll/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar facturas' }, false, 'fc/fetchAll/error');
        }
      },

      fetchById: async (id: number) => {
        set({ loading: true, error: null }, false, 'fc/fetchById/start');
        try {
          const item = await FacturaCompraService.getById(id);
          set({ selectedItem: item, loading: false }, false, 'fc/fetchById/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al cargar factura' }, false, 'fc/fetchById/error');
        }
      },

      save: async (data: FacturaCompra) => {
        set({ loading: true, error: null }, false, 'fc/save/start');
        try {
          await FacturaCompraService.save(data);
          set({ loading: false }, false, 'fc/save/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al guardar' }, false, 'fc/save/error');
          throw error;
        }
      },

      remove: async (id: number) => {
        set({ loading: true, error: null }, false, 'fc/delete/start');
        try {
          await FacturaCompraService.delete(id);
          set((state) => ({
            items: state.items.filter((i) => i.idFacturaCompra !== id),
            loading: false,
          }), false, 'fc/delete/success');
        } catch (error) {
          set({ loading: false, error: error instanceof Error ? error.message : 'Error al eliminar' }, false, 'fc/delete/error');
        }
      },

      setSelectedItem: (item) => set({ selectedItem: item }, false, 'fc/setSelected'),
      clearError: () => set({ error: null }, false, 'fc/clearError'),
      reset: () => set(initialState, false, 'fc/reset'),
    }),
    { name: 'factura-compra-store' }
  )
);
